import {
	COMPLETE_STOPWORD_SETS,
	GLOBAL_STOPWORD_SET
} from './stopwords';

// Pre-compiled regex patterns for performance
const UNICODE_WORD_REGEX = /[\p{L}\p{N}\p{Sc}\p{M}\u0026#']{1,}/giu;
const ESCAPE_REGEX = /[.*+?^${}()|[\]\\]/g;
const MIN_KEYWORDS_FOR_CLUSTERING = 3;

/**
 * Extract significant n-grams from keywords
 * @param {Array} keywordList - Array of keyword objects [{_id, kw, sv, cluster}, ...]
 * @param {Array} ngramSizes - Array of n-gram sizes to extract [1, 2, 3] = unigrams, bigrams, trigrams
 * @param {number} minWordLength - Minimum word length to include (filters short words)
 * @param {number} minNgramFrequency - Minimum frequency for n-gram to be considered significant
 * @param {string|null} userLanguage - Optional language override (null = auto-detect)
 * @returns {Array} Array of significant n-gram strings
 */
function extractNgrams(
	keywordList,
	ngramSizes = [1, 2, 3],
	minWordLength = 3,
	minNgramFrequency = 2,
	userLanguage = null
) {
	let language;
	let stopwordsSet;
	if (userLanguage) {
		language = userLanguage;
		stopwordsSet = COMPLETE_STOPWORD_SETS[language];
		if (!stopwordsSet) {
			console.debug(
				`Clustering - Warning: No stopwords will be used for unsupported language. ${language}`
			);
			stopwordsSet = GLOBAL_STOPWORD_SET;
		}
		// console.debug(`Clustering - Using language override: ${language}`);
	} else {
		// language = detectLanguage(keywordList);
		// if (!language) {
		// 	console.debug('Clustering - Language detection failed, defaulting to English');
		// 	language = 'en';
		// }
		stopwordsSet = GLOBAL_STOPWORD_SET;
	}


	// Use Map for better performance with frequent lookups
	const ngramCounts = new Map();

	for (const keywordObj of keywordList) {
		const words =
			keywordObj.kw.toLowerCase().match(UNICODE_WORD_REGEX) || [];
		const cleanWordsForKeyword = words.filter(
			(word) => !stopwordsSet.has(word) && word.length >= minWordLength
		);

		for (const n of ngramSizes) {
			if (cleanWordsForKeyword.length >= n) {
				for (let i = 0; i <= cleanWordsForKeyword.length - n; i++) {
					const ngram = cleanWordsForKeyword
						.slice(i, i + n)
						.join(' ');
					ngramCounts.set(ngram, (ngramCounts.get(ngram) || 0) + 1);
				}
			}
		}
	}

	// Filter and sort in one pass, avoid intermediate arrays
	const significantNgrams = [];
	for (const [ngram, frequency] of ngramCounts) {
		if (frequency >= minNgramFrequency) {
			significantNgrams.push({
				ngram,
				length: ngram.split(' ').length,
				frequency
			});
		}
	}

	significantNgrams.sort((a, b) => {
		if (a.length !== b.length) {
			return b.length - a.length;
		}
		return b.frequency - a.frequency;
	});

	return significantNgrams.map((item) => item.ngram);
}

/**
 * Process keywords and return structured cluster data
 * @param {Array} keywordList - Array of keyword objects [{_id, kw, sv, cluster}, ...] - cluster will be added
 * NOTE: This function mutates the keywordList objects by adding a 'cluster' property to each keyword object
 * @param {string|null} languageCode - Optional language override (null = auto-detect)
 * @param {Object} options - Options object
 * @param {number} options.minWordLength - Minimum word length to include (filters short words)
 * @param {number} options.minNgramFrequency - Minimum frequency for n-gram to be considered significant
 * @param {number} options.minClusterSize - Minimum size for a cluster to be considered significant
 * @param {number} options.minTermLength - Minimum length for a term to be considered significant
 * @param {Array} options.ngramSizes - Array of n-gram sizes to extract [1, 2, 3] = unigrams, bigrams, trigrams
 * @param {number|null} options.maxClusters - Maximum number of clusters to return (null = no limit)
 * @returns {Array} Array of cluster objects
 *
 * Performance Tips for Large Datasets (1000+ keywords):
 * - Limit n-gram sizes for speed: { ngramSizes: [1, 2] } (skip trigrams)
 * - Increase thresholds to reduce terms: { minNgramFrequency: 3, minTermLength: 5 }
 * - Expected runtime for 1000 keywords: 2-10 seconds depending on complexity
 */
function processCluster(keywordList, languageCode = null, options = {}) {
	// Early return for small datasets - skip clustering if not enough keywords
	if (keywordList.length < MIN_KEYWORDS_FOR_CLUSTERING) {
		// console.debug(
		// 	`Clustering - Skipping clustering: only ${keywordList.length} keywords (minimum: ${MIN_KEYWORDS_FOR_CLUSTERING})`
		// );
		return [];
	}

	const {
		minWordLength = 3,
		minNgramFrequency = 2,
		minClusterSize = 3,
		minTermLength = 4,
		ngramSizes = [1, 2, 3],
		maxClusters = null
	} = options;

	// Skip processing if not enough keywords for minimum cluster size
	if (keywordList.length < minClusterSize) {
		// console.debug(
		// 	`Clustering - Skipping clustering: ${keywordList.length} keywords < minClusterSize (${minClusterSize})`
		// );
		return [];
	}


	const significantTerms = extractNgrams(
		keywordList,
		ngramSizes,
		minWordLength,
		minNgramFrequency,
		languageCode
	);

	// Pre-allocate data structures for better performance
	const termClusters = {};
	const termVolumes = {};
	const keywordToCluster = new Map(); // Replace Set with Map for better performance

	// Pre-create regex patterns for reuse
	const termRegexMap = new Map();

	for (const term of significantTerms) {
		if (term.length < minTermLength) {
			// eslint-disable-next-line no-continue
			continue;
		}

		// Create regex once and store it
		const escapedTerm = term.replace(ESCAPE_REGEX, '\\$&');
		// Use Unicode word boundaries for cyrillic support
		const regex = new RegExp(`(?<!\\p{L})${escapedTerm}(?!\\p{L})`, 'u');
		termRegexMap.set(term, regex);

		const cluster = [];
		let clusterVolume = 0;

		for (const keywordObj of keywordList) {
			if (!keywordToCluster.has(keywordObj.kw)) {
				if (regex.test(keywordObj.kw)) {
					cluster.push(keywordObj);
					clusterVolume += keywordObj.sv;
				}
			}
		}

		if (cluster.length >= minClusterSize) {
			termClusters[term] = cluster;
			termVolumes[term] = clusterVolume;
			cluster.forEach((keywordObj) => {
				keywordToCluster.set(keywordObj.kw, term);
				keywordObj.cluster = term;
			});
		}
	}

	const sortedClusters = Object.keys(termClusters).sort(
		(a, b) => termVolumes[b] - termVolumes[a]
	);

	// Apply maxClusters limit if specified
	if (maxClusters && maxClusters > 0) {
		const topClusters = sortedClusters.slice(0, maxClusters);
		const removedClusters = sortedClusters.slice(maxClusters);

		// Reassign keywords from removed clusters to nearest top cluster
		for (const term of removedClusters) {
			const cluster = termClusters[term];
			for (const keywordObj of cluster) {
				keywordToCluster.delete(keywordObj.kw);
				keywordObj.cluster = null;

				// Find best matching top cluster using pre-created regex
				for (const topTerm of topClusters) {
					const regex = termRegexMap.get(topTerm);
					if (regex && regex.test(keywordObj.kw)) {
						termClusters[topTerm].push(keywordObj);
						termVolumes[topTerm] += keywordObj.sv;
						keywordToCluster.set(keywordObj.kw, topTerm);
						keywordObj.cluster = topTerm;
						break;
					}
				}
			}
		}

		// Update sortedClusters to only include top clusters
		sortedClusters.length = maxClusters;
	}

	// Create final clusters - calculate everything only once
	// Pre-allocate array with known size for better performance
	const clusterCount =
		maxClusters && maxClusters < sortedClusters.length
			? maxClusters
			: sortedClusters.length;
	const clusters = new Array(clusterCount);

	for (let i = 0; i < clusterCount; i++) {
		const term = sortedClusters[i];
		const cluster = termClusters[term];
		const primaryKeyword = cluster.reduce((max, kw) =>
			kw.sv > max.sv ? kw : max
		);
		clusters[i] = {
			name: term,
			primaryKeyword: primaryKeyword._id
		};
	}

	// Cache length calculation for console output
	// const totalKeywords = keywordList.length;
	// const clusteredKeywords = keywordToCluster.size;
	// console.debug(`Clustering - Created ${clusters.length} clusters from ${clusteredKeywords} keywords`);
	// console.debug(`Clustering - Unclustered keywords: ${totalKeywords - clusteredKeywords}`);

	return clusters;
}

export { extractNgrams, processCluster };
